/**
 * Everyone else online: a figure each where the server last saw them (a little in the past, so their
 * walks look smooth), with their name over them, or an arrow at the screen's edge when out of sight.
 */
import { walkNearest, type Grid } from '@bramvasterd/terrella-rules';
import * as THREE from 'three';

import type { Loop } from '@core/loop';
import { colorFor } from '@core/palette';
import { vec } from '@core/quat';
import type { Game } from '@game/game';
import { DISPLAY_DELAY_MS, positionAt } from '@game/player/presence';

import { screenPoint, type Projector } from '../overlays/projector';
import { createTag, type Tag } from '../overlays/tags';
import type { Planet } from '../planet/planet';
import { onPlanet, type PlanetPose } from '../planet/pose';
import { MAX_FIGURES, type Figures } from './figures';

const UP = new THREE.Vector3(0, 1, 0);
/** How high a name floats over a figure. */
const NAME_LIFT = 0.95;

export interface Mates {
  /** Place them as figures 1 and up; returns how many figures there are, me included. */
  place(now: number, pose: PlanetPose, grid: Grid): number;
  /** Outside the game: their names go. */
  hide(): void;
  remove(): void;
}

export interface MatesDeps {
  game: Game;
  figures: Figures;
  planet: Planet;
  projector: Projector;
  /** Where the name tags go. */
  root: HTMLElement;
  nameOf: (player: string) => string;
  loop: Loop;
  reduced: boolean;
}

export function createMates({
  game,
  figures,
  planet,
  projector,
  root,
  nameOf,
  loop,
  reduced,
}: MatesDeps): Mates {
  const tags = new Map<string, Tag>();
  const tileOf = new Map<string, number>();
  const seen = new Set<string>();
  const p = vec();
  const h = vec();
  const dir = new THREE.Vector3();
  const pos = new THREE.Vector3();
  const up = new THREE.Vector3();
  const head = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const facing = new THREE.Vector3();
  const cross = new THREE.Vector3();
  const turn = new THREE.Quaternion();
  const twist = new THREE.Quaternion();
  const tint = new THREE.Color();
  const screen = screenPoint();

  /** Their name at `head` while it's on screen, else an arrow at the edge pointing their way. */
  const placeName = (tag: Tag): void => {
    const { width, height } = projector;
    const { x, y, behind, facing: seenFromHere } = projector.project(head, -0.2, screen);
    if (seenFromHere && !behind && x > 8 && x < width - 8 && y > 8 && y < height - 8) {
      tag.showAt(x, y);
      return;
    }
    const cx = width / 2;
    const cy = height / 2;
    let vx = behind ? cx - x : x - cx;
    let vy = behind ? cy - y : y - cy;
    if (Math.hypot(vx, vy) < 1) {
      vx = 0;
      vy = -1;
    }
    const k = Math.min(
      (width / 2 - 70) / Math.abs(vx || 1e-6),
      (height / 2 - 130) / Math.abs(vy || 1e-6),
    );
    tag.showEdge(cx + vx * k, cy + vy * k, Math.atan2(vy, vx));
  };

  /** Turn `turn` (upright at `up`) to face along `facing`, flattened onto the ground. */
  const face = (): void => {
    facing.addScaledVector(up, -facing.dot(up));
    if (facing.lengthSq() <= 1e-6) return;
    facing.normalize();
    forward.set(0, 0, 1).applyQuaternion(turn);
    const angle = Math.atan2(cross.crossVectors(forward, facing).dot(up), forward.dot(facing));
    turn.multiply(twist.setFromAxisAngle(UP, angle));
  };

  return {
    place(now, pose, grid) {
      const s = game.store.get();
      const t = (game.clock.serverNow() ?? 0) - DISPLAY_DELAY_MS;
      let n = 1;
      seen.clear();
      for (const mate of s.online.values()) {
        if (n >= MAX_FIGURES) break;
        seen.add(mate.id);
        const moving = positionAt(mate, t, p, h);
        dir.set(p[0] ?? 0, p[1] ?? 1, p[2] ?? 0);
        const last = tileOf.get(mate.id) ?? 0;
        const tile = walkNearest(grid, dir.x, dir.y, dir.z, last < grid.count ? last : 0);
        tileOf.set(mate.id, tile);
        onPlanet(pose, dir, planet.tileTop(tile) - 0.01, pos);
        up.copy(dir).applyQuaternion(pose.quaternion);
        turn.setFromUnitVectors(UP, up);
        facing.set(h[0] ?? 0, h[1] ?? 0, h[2] ?? 1).applyQuaternion(pose.quaternion);
        face();
        const bob = reduced || !moving ? 0 : Math.abs(Math.sin(now / 90)) * 0.045;
        const color = s.members.get(mate.id)?.color ?? colorFor(mate.id);
        figures.set(n++, pos, turn, bob, tint.set(color));
        if (moving) loop.wake(200);
        let tag = tags.get(mate.id);
        if (!tag) {
          tag = createTag(root);
          tags.set(mate.id, tag);
        }
        tag.setLook(nameOf(mate.id), color);
        head.copy(pos).addScaledVector(up, NAME_LIFT);
        placeName(tag);
      }
      for (const [id, tag] of tags) {
        if (seen.has(id)) continue;
        tag.remove();
        tags.delete(id);
        tileOf.delete(id);
      }
      return n;
    },
    hide() {
      for (const tag of tags.values()) tag.hide();
    },
    remove() {
      for (const tag of tags.values()) tag.remove();
      tags.clear();
      tileOf.clear();
    },
  };
}
